import { useCallback, useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/Container";
import { Stack } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/layout";
import {
  Avatar,
  Flex,
  HStack,
  Box,
  useToast,
  useDisclosure,
  Button,
  IconButton,
} from "@chakra-ui/react";
import { AccountProfileForm } from "../../components/AccountProfileForm";
import { UserProfileForm } from "../../components/UserProfileForm";
import { FileUploadModal } from "../../components/FileUploadModal";
import { Icon } from "../../components/Icon";
import { TrialBanner } from "../../components/TrialBanner";
import { PrimayButton } from "../../components/Button";
import { isMobile } from "react-device-detect";
import ReactGA from "react-ga";

export const Profile: React.FC = observer(() => {
  const { session, clientsStore } = useStores();
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();
  const [subscriptionNotActivated, setSubscriptionNotActivated] =
    useState(false);

  useEffect(() => {
    ReactGA.pageview("/client/profile");
  }, []);

  const handleChangePassword = useCallback(
    async (data) => {
      try {
        await session.changeSessionUserPassword(data.newPassword);
        toast({
          status: "success",
          title: t("common.actionExecutedSuccessfully"),
        });
      } catch (err) {
        console.log(err);
        toast({
          status: "error",
          title: t("common.error"),
        });
      }
    },
    [session, t, toast]
  );

  const handleUpdateProfile = useCallback(
    async (data) => {
      if (session.user?.id) {
        try {
          await clientsStore.updateCustomer(session.user?.id, data);
          toast({
            status: "success",
            title: t("common.actionExecutedSuccessfully"),
          });
        } catch (err) {
          console.log(err);
          toast({
            status: "error",
            title: t("common.error"),
          });
        }
      }
    },
    [clientsStore, session, t, toast]
  );

  const handleUploadPhoto = useCallback(
    async (imageUrls: string[]) => {
      await handleUpdateProfile({
        picture: imageUrls[0],
      });
      onClose();
    },
    [handleUpdateProfile, onClose]
  );

  const handleCheckout = useCallback(async () => {
    setSubscriptionNotActivated(false);
    try {
      await session.stripeCheckout();
    } catch (err) {
      console.log(err);
    }
  }, [session]);

  const handlePortalAccess = useCallback(async () => {
    // setStripePortalSuccess(false);
    try {
      const portalSessionUrl = await session.stripeAccessPortal();
      // Redirect to Portal
      if (!!portalSessionUrl) {
        window.location.replace(portalSessionUrl);
      }
    } catch (err) {
      console.log(err);
    }
  }, [session]);

  if (!session.user) {
    return <Redirect to={"/"} />;
  }

  return (
    <Stack margin={"2rem"} paddingBottom={"2rem"}>
      <HStack marginBottom={"1rem"}>
        <IconButton
          aria-label="Back"
          icon={<Icon iconName={"HiArrowLeft"} />}
          variant={"ghost"}
          onClick={() => history.push("/client/board")}
        />
        <Text fontSize="xl" fontWeight={300}>
          {t("profile.profile")}
        </Text>
      </HStack>

      <Stack
        marginLeft={isMobile ? "0" : "0"}
        alignContent="center"
        alignItems="center"
        gridGap={"2rem"}
      >
        <HStack
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          alignItems={isMobile ? "" : "flex-end"}
          w={"100%"}
          // alignSelf="center"
        >
          <Container
            width={isMobile ? "100%" : "55%"}
            // maxW="28.125rem"
            mr={isMobile ? "0px" : "40px"}
            mb={"0px"}
          >
            <Text fontSize="3xl" mb="15px">
              {t("profile.myAccount")}
            </Text>
            <AccountProfileForm
              user={session.user}
              onSubmit={handleChangePassword}
              isSubmitting={session.isSubmitting}
            />
            <Stack spacing={"1rem"}></Stack>
          </Container>
          <Flex
            width={isMobile ? "100%" : "45%"}
            // maxW="28.125rem"
            ml={isMobile ? "0px !important" : "40px"}
            mb={isMobile ? "40px" : "0px"}
            flexDirection="column"
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <Avatar size="2xl" src={session.user.picture} />
              <Button variant={"ghost"} onClick={onOpen} marginTop={"0.5rem"}>
                {t("profile.changeImage")}
              </Button>
              <FileUploadModal
                isOpen={isOpen}
                onClose={onClose}
                onFileUploaded={handleUploadPhoto}
                accept={"image/jpeg, image/png"}
              />
            </Flex>

            <Container mt="35px" mb={isMobile ? "38px" : "0"}>
              <Box
                display="flex"
                flex={1}
                flexDirection="column"
                justifyContent="space-between"
              >
                <Stack spacing={6}>
                  <TrialBanner
                    isTrialMode={session.isTrialMode}
                    signUpDate={session.user?.createdAt}
                    trialPeriod={7}
                  />
                </Stack>
                <Stack spacing={"1rem"}>
                  {session.isTrialMode ||
                  session.isSubscriptionDeactivated ||
                  session.isPTSubscription ? (
                    <PrimayButton onClick={handleCheckout}>
                      {t("profile.activateSubscription")}
                    </PrimayButton>
                  ) : (
                    <PrimayButton onClick={handlePortalAccess}>
                      {t("profile.manageSubscription")}
                    </PrimayButton>
                  )}
                </Stack>
              </Box>
            </Container>
          </Flex>
        </HStack>

        <Container w={"100%"}>
          <Box flex={1}>
            <Text fontSize="3xl" mb="15px">
              {t("profile.myProfile")}
            </Text>

            <UserProfileForm
              user={session.user}
              onSubmit={handleUpdateProfile}
              isSubmitting={clientsStore.isUpdatingCustomerProfile}
              validable={true}
            />
          </Box>
        </Container>
      </Stack>
    </Stack>
  );
});
